import { useEffect } from 'react';
import type { AppProps } from 'next/app';
import App from '~/components/layout/App';
import { pageIndicatorMaatEvent } from '~/service/maatEvent';
import '../styles/globals.css';
import AppMountedWrapper from '~/common/appMountedWrapper';

function AppEntry({ Component, pageProps }: AppProps) {
  useEffect(() => {
    // 初始化
    new AppMountedWrapper().install();
    //
    import('~/common/vconsole/index');
  }, []);

  useEffect(() => {
    const setAppHeight = () => {
      const doc = document.documentElement;
      const innerHeight = window.innerHeight || 0;
      const clientHeight = (document.documentElement && document.documentElement.clientHeight) || 0;
      const visualViewportHeight =
        (window.visualViewport && typeof window.visualViewport.height === 'number'
          ? window.visualViewport.height
          : 0) || 0;
      const candidates = [innerHeight, clientHeight, visualViewportHeight].filter(
        (h) => typeof h === 'number' && h > 0
      ) as number[];
      const minHeight = candidates.length ? Math.min(...candidates) : innerHeight;
      const screenHeight = window.screen && typeof window.screen.height === 'number' ? window.screen.height : 0;
      // 设置可信高度 如果屏幕高度大于0且最小高度大于屏幕高度的50%，则设置app高度
      if (screenHeight > 0 && minHeight > screenHeight * 0.5) {
        doc.style.setProperty('--app-height', `${Math.round(minHeight)}px`);
      }
      try {
        pageIndicatorMaatEvent('global_app_height', {
          app_height: Math.round(minHeight),
          screen_height: screenHeight,
        });
      } catch (_) {
        // ignore
      }
    };

    // 使用 pageshow/visibilitychange + 连续三帧 rAF 做首测量，规避 IAB 冷启动小值
    let rafId1 = 0;
    let rafId2 = 0;
    let rafId3 = 0;
    const rafCompute = () => {
      if (typeof window.requestAnimationFrame !== 'function') {
        setAppHeight();
        return;
      }
      rafId1 = window.requestAnimationFrame(() => {
        rafId2 = window.requestAnimationFrame(() => {
          rafId3 = window.requestAnimationFrame(() => {
            setAppHeight();
          });
        });
      });
    };

    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        rafCompute();
      }
    };

    const onPageShow = () => {
      rafCompute();
    };

    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      rafCompute();
    } else {
      document.addEventListener('visibilitychange', onVisibilityChange, { once: true });
    }
    window.addEventListener('pageshow', onPageShow, { once: true });
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
      if (rafId3) cancelAnimationFrame(rafId3);
    };
  }, []);

  return (
    <App>
      <Component {...pageProps} />
    </App>
  );
}

export default AppEntry;
